// src/components/session/SessionPanel.js
// "Your session": resume link (copy / create new), retention info, delete.
import React, { useState } from "react";
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, InputAdornment,
  Link, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LinkIcon from "@mui/icons-material/Link";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { createResumeLink, deleteSession } from "../../api";
import { clearAllMirrors } from "../../hooks/useSessionSync";
import { copyText } from "../../utils/clipboard";
import { friendlyError } from "../../utils/errors";

const LINK_KEY = "t4r_ljd_resume_link_v1";
const readLink = () => {
  try { return window.localStorage.getItem(LINK_KEY) || ""; } catch { return ""; }
};
const writeLink = (v) => {
  try { if (v) window.localStorage.setItem(LINK_KEY, v); else window.localStorage.removeItem(LINK_KEY); } catch { /* ignore */ }
};

export default function SessionPanel({ open, onClose, summary }) {
  const [link, setLink] = useState(readLink);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const expires = summary?.session?.expires_at ? new Date(summary.session.expires_at) : null;

  const makeLink = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const res = await createResumeLink();
      setLink(res.resume_url);
      writeLink(res.resume_url);
      setMessage({ type: "success", text: link ? "New link created. The old link no longer works." : "Link created." });
    } catch (err) {
      setMessage({ type: "error", text: friendlyError(err) });
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    const ok = await copyText(link);
    setMessage(ok ? { type: "success", text: "Link copied." } : { type: "error", text: "Could not copy. Select the link and copy it manually." });
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await deleteSession();
      writeLink("");
      clearAllMirrors();
      window.location.assign("/");
    } catch (err) {
      setMessage({ type: "error", text: friendlyError(err) });
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="session-panel-title">
      <DialogTitle id="session-panel-title" sx={{ fontWeight: 700 }}>Your session</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Your progress is saved automatically. To continue later or on another device, use a resume link.
          Anyone with the link can open your journey, so treat it like a password.
        </Typography>

        {link ? (
          <TextField
            label="Resume link"
            value={link}
            fullWidth
            size="small"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Copy link">
                    <IconButton aria-label="Copy resume link" onClick={copy} edge="end"><ContentCopyIcon /></IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            onFocus={(e) => e.target.select()}
          />
        ) : null}
        <Button sx={{ mt: 1.5 }} startIcon={<LinkIcon />} variant={link ? "text" : "contained"} onClick={makeLink} disabled={busy}>
          {link ? "Create a new link (the old one stops working)" : "Create resume link"}
        </Button>

        {message && <Alert severity={message.type} sx={{ mt: 2 }} role="status">{message.text}</Alert>}

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle2" gutterBottom>Your data</Typography>
        <Typography variant="body2" color="text.secondary">
          We store your answers, the AI results and the status of each step. Everything is deleted automatically
          30 days after your last activity{expires ? ` (currently on ${expires.toLocaleDateString()})` : ""}.{" "}
          <Link href="/privacy/" target="_blank" rel="noopener">Privacy and cookies</Link>
        </Typography>

        <Box sx={{ mt: 2 }}>
          {!confirmDelete ? (
            <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={() => setConfirmDelete(true)}>
              Delete my session
            </Button>
          ) : (
            <Alert
              severity="warning"
              action={
                <Stack direction="row" spacing={1}>
                  <Button color="inherit" size="small" onClick={() => setConfirmDelete(false)}>Cancel</Button>
                  <Button color="error" variant="contained" size="small" onClick={doDelete} disabled={busy}>Delete now</Button>
                </Stack>
              }
            >
              This permanently deletes all answers and results of this journey. It cannot be undone.
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
